import {
  Chart as ChartJS,
  CategoryScale,
  ChartData,
  ChartOptions,
  InteractionItem,
} from "chart.js/auto";
import { getElementAtEvent, Line } from "react-chartjs-2";
import styled from "styled-components";
import "chartjs-adapter-moment";
import { useRef } from "react";
import AASpinner from "../AASpinner";
import Color from "color";
import { ColorAA } from "../constants";

ChartJS.register(CategoryScale);

const LinePlotContainer = styled.div<{ $height: string }>`
  width: 100%;
  height: ${({ $height }) => $height};
  background-color: var(--aa-surface-bg);
  padding: 20px;
  border-radius: 6px;
`;

export const getDefaultOptions = ({
  isDarkMode,
}: {
  isDarkMode: boolean;
}): ChartOptions<"line"> => {
  const textColor = isDarkMode ? ColorAA.lightBeige : ColorAA.lightBlack;
  const mutedColor = Color(isDarkMode ? ColorAA.beige : ColorAA.grey)
    .alpha(0.8)
    .hexa();
  const tooltipBg = isDarkMode ? ColorAA.lightBlack : ColorAA.white;

  return {
    animation: false,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 3,
          color: mutedColor,
        },
      },
      y: {
        ticks: {
          stepSize: 1,
          maxTicksLimit: 4,
          color: mutedColor,
        },
        border: {
          color: mutedColor,
        },
        grid: {
          color: mutedColor,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: textColor,
          font: {
            family: "monospace",
            size: 14,
          },
        },
      },
      tooltip: {
        titleColor: mutedColor,
        bodyColor: textColor,
        backgroundColor: tooltipBg,
        titleFont: {
          family: "Helvetica Neue",
          size: 14,
        },
        bodyFont: {
          family: "Helvetica Neue",
          size: 14,
        },
      },
    },
  };
};

const LinePlot = ({
  data,
  options = getDefaultOptions({ isDarkMode: false }),
  height = "250px",
  onClick,
}: {
  data?: ChartData<"line", object[], string>;
  options?: ChartOptions<"line">;
  height?: string;
  onClick?: (element: Array<InteractionItem>) => void;
}) => {
  const chartRef = useRef();
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (chartRef.current) {
      const elements = getElementAtEvent(chartRef.current, e);
      if (onClick && elements?.length) {
        onClick(elements);
      }
    }
  };

  return (
    <LinePlotContainer $height={height}>
      {data ? (
        <Line
          ref={chartRef}
          data={data}
          options={options}
          onClick={handleClick}
        />
      ) : (
        <AASpinner variant="light" />
      )}
    </LinePlotContainer>
  );
};

export default Object.assign(LinePlot, { getDefaultOptions });
